/** Finding a station by what a reader remembers of it: part of its name,
 *  its code, the id on the logger or gateway, who makes it, or a project it
 *  is in (only the reader's projects reach the page). Shared by
 *  the Stations overview and the station picker, so both find the same
 *  stations for the same words. */
import type { Station } from '../api/types'
import { VENDOR_LABEL } from '../api/types'

export type Searchable = Pick<Station, 'code' | 'name' | 'external_id' | 'vendor' | 'projects'>

/** The words of a query or a field, lower-cased. Anything that is not a
 *  letter or a digit separates words, so a hyphen, a comma and a space are
 *  read alike: "dzveli khibula" finds `dzveli-khibula`. Georgian letters
 *  are letters. */
export function searchTerms(text: string): string[] {
  return text.toLowerCase().split(/[^\p{L}\p{N}]+/u).filter(Boolean)
}

function haystack(station: Searchable): string {
  return searchTerms([station.name, station.code, station.external_id,
                      VENDOR_LABEL[station.vendor] ?? station.vendor,
                      ...(station.projects ?? [])].join(' '))
    .join(' ')
}

/** Every word of the query appears somewhere in the station's fields, in
 *  any order: "nea mam" narrows as a reader types it, where a whole-phrase
 *  match would find nothing until the phrase was exactly right. */
export function matchesStation(station: Searchable, query: string): boolean {
  const terms = searchTerms(query)
  if (!terms.length) return true
  const text = haystack(station)
  return terms.every((term) => text.includes(term))
}

/** The stations the query finds, in the order given. */
export function filterStations<T extends Searchable>(stations: T[], query: string): T[] {
  return stations.filter((s) => matchesStation(s, query))
}

/** The picker's highlighted row after an arrow key, wrapping at either end
 *  as a list a reader is scanning should; -1 when there is nothing to
 *  highlight. From nothing highlighted, down starts at the top and up at
 *  the bottom. */
export function moveActive(active: number, delta: number, count: number): number {
  if (count <= 0) return -1
  if (active < 0) return delta > 0 ? 0 : count - 1
  return (active + delta + count) % count
}

/** What Enter opens: the highlighted station, or else the first match, so
 *  typing "gomb" and pressing Enter is enough. Null when nothing matches. */
export function pickOnEnter(matches: Searchable[], active: number): string | null {
  return (matches[active] ?? matches[0])?.code ?? null
}
